import React, { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { useAccountCancel } from "../../hooks/auth/useAccountCancel";

interface WithdrawModalProps {
  isOpen: boolean;
  onClose: () => void;
  userNickname?: string;
}

const WithdrawModal = ({
  isOpen,
  onClose,
  userNickname = "회원",
}: WithdrawModalProps) => {
  const [step, setStep] = useState<"warning" | "confirm">("warning");
  const { isLoading: isProcessing, error, accountCancel } = useAccountCancel();

  const handleWithdraw = async () => {
    const success = await accountCancel();
    if (success) {
      alert(
        "회원탈퇴가 처리되었습니다.\n그동안 Aivy를 이용해 주셔서 감사합니다."
      );
    }
  };

  const handleClose = () => {
    setStep("warning");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 font-serif-kr">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">회원탈퇴</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* 내용 */}
        <div className="p-6">
          {step === "warning" ? (
            <>
              {/* 경고 안내 */}
              <div className="mb-6">
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-red-900 mb-2">
                      회원탈퇴 시 주의사항
                    </h3>
                    <p className="text-red-800 text-sm">
                      한번 탈퇴하면 복구할 수 없습니다.
                    </p>
                  </div>
                </div>

                <div className="pb-4 ml-2">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    회원탈퇴 시 삭제되는 정보:
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span>- 계정 정보 (이메일, 닉네임 등)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>- 생성한 AI 에이전트 및 관련 데이터</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>- 사용 히스토리</span>
                    </li>
                  </ul>
                </div>

                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>참고:</strong> 개인정보보호법에 따라 일부 정보는
                    법정 보관기간 동안 보관됩니다.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  취소
                </button>
                <button
                  onClick={() => setStep("confirm")}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  탈퇴 진행
                </button>
              </div>
            </>
          ) : (
            <>
              {/* 확인 단계 */}
              <div className="mb-6">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    정말로 탈퇴하시겠습니까?
                  </h3>
                  <p className="text-gray-600">
                    <strong>{userNickname}</strong>님의 모든 데이터가
                    삭제됩니다.
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep("warning")}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50"
                >
                  이전
                </button>
                <button
                  onClick={handleWithdraw}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? "처리 중..." : "회원탈퇴"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawModal;
